"use client"

import { useState } from "react"
import { Link as LinkIcon, Truck } from "lucide-react"

import { ConnectDeliveryIntegrationDialog } from "@/components/deliveries/connect-delivery-integration-dialog"
import { DeliveryIntegrationItem } from "@/components/deliveries/delivery-integration-item"
import { DeliveryPiiRetentionForm } from "@/components/deliveries/delivery-pii-retention-form"
import { DeliveryToolsRecommendation } from "@/components/deliveries/delivery-tools-recommendation"
import { DisconnectDeliveryIntegrationDialog } from "@/components/deliveries/disconnect-delivery-integration-dialog"
import { EditDeliveryIntegrationDialog } from "@/components/deliveries/edit-delivery-integration-dialog"
import { DeliveryShipmentsPanel } from "@/components/deliveries/delivery-shipments-panel"
import { DeliveryTenantDefaultsForm } from "@/components/deliveries/delivery-tenant-defaults-form"
import { IntegrationCardLayout } from "@/components/integrations/integration-card-layout"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  useDisconnectDeliveryIntegrationMutation,
  useTestDeliveryIntegrationMutation,
  useToggleDeliveryIntegrationMutation,
} from "@/hooks/mutations/delivery-integrations"
import { useDeliveryIntegrationsQuery } from "@/hooks/queries/delivery-integrations"
import type { DeliveryIntegrationView } from "@/server-actions/delivery-integrations"
import { useTranslations } from "next-intl"


type DeliveryIntegrationListState = "loading" | "empty" | "ready"

export function DeliveryIntegrationCard({
  defaultOpen = false,
}: {
  defaultOpen?: boolean
}) {
  const tUi = useTranslations("Deliveries.DeliveriesDeliveryIntegrationCard")
  const tCommonUi = useTranslations("Common")

  const integrationsQuery = useDeliveryIntegrationsQuery()
  const testMutation = useTestDeliveryIntegrationMutation()
  const toggleMutation = useToggleDeliveryIntegrationMutation()
  const disconnectMutation = useDisconnectDeliveryIntegrationMutation()
  const [connectOpen, setConnectOpen] = useState(false)
  const [editing, setEditing] = useState<DeliveryIntegrationView | null>(null)
  const [disconnecting, setDisconnecting] = useState<DeliveryIntegrationView | null>(null)
  const integrations = integrationsQuery.data ?? []
  const integrationListState = getIntegrationListState(
    integrationsQuery.isLoading,
    integrations.length,
  )
  const hasConnectedIntegration = integrations.some(integration => (
    integration.isActive && integration.verificationStatus === "succeeded"
  ))

  return (
    <>
      <IntegrationCardLayout
        title={tUi("deliveries")}
        description={tUi("connectACarrierSoAgentsCanQuoteDelivery")}
        icon={<Truck aria-hidden="true" className="h-5 w-5" />}
        defaultOpen={defaultOpen}
      >
        <div className="space-y-4">
          {integrationsQuery.error ? (
            <Alert variant="destructive">
              <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                <span><span key={tUi("deliveryIntegrationsCouldNotBeLoaded")}>{tUi("deliveryIntegrationsCouldNotBeLoaded")}</span></span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => void integrationsQuery.refetch()}
                >
                  <span key={tCommonUi("retry")}>{tCommonUi("retry")}</span></Button>
              </AlertDescription>
            </Alert>
          ) : null}

          <DeliveryIntegrationList
            state={integrationListState}
            integrations={integrations}
            testingId={testMutation.variables}
            testPending={testMutation.isPending}
            togglingId={toggleMutation.variables?.integrationId}
            togglePending={toggleMutation.isPending}
            disconnectingId={disconnectMutation.variables}
            disconnectPending={disconnectMutation.isPending}
            onEdit={setEditing}
            onTest={integrationId => testMutation.mutate(integrationId)}
            onToggle={(integrationId, active) => toggleMutation.mutate({
              integrationId,
              active,
            })}
            onDisconnect={integrationId => setDisconnecting(
              integrations.find(item => item.id === integrationId) ?? null,
            )}
          />

          <Button type="button" onClick={() => setConnectOpen(true)}>
            <LinkIcon aria-hidden="true" className="mr-2 h-4 w-4" />
            <span><span key={tUi("connectDeliveryProvider")}>{tUi("connectDeliveryProvider")}</span></span>
          </Button>

          {hasConnectedIntegration ? (
            <>
              <DeliveryToolsRecommendation />
              <DeliveryTenantDefaultsForm integrations={integrations} />
              <DeliveryPiiRetentionForm />
              <DeliveryShipmentsPanel />
            </>
          ) : null}
        </div>
      </IntegrationCardLayout>

      <ConnectDeliveryIntegrationDialog
        open={connectOpen}
        onOpenChange={setConnectOpen}
      />
      <EditDeliveryIntegrationDialog
        integration={editing}
        onOpenChange={open => { if (!open) setEditing(null) }}
      />
      <DisconnectDeliveryIntegrationDialog
        open={disconnecting !== null}
        integrationName={disconnecting?.name ?? "delivery integration"}
        busy={disconnectMutation.isPending}
        onOpenChange={open => { if (!open) setDisconnecting(null) }}
        onConfirm={() => {
          if (!disconnecting) return
          disconnectMutation.mutate(disconnecting.id, {
            onSuccess: () => setDisconnecting(null),
          })
        }}
      />
    </>
  )
}

function DeliveryIntegrationList({
  state,
  integrations,
  testingId,
  testPending,
  togglingId,
  togglePending,
  disconnectingId,
  disconnectPending,
  onEdit,
  onTest,
  onToggle,
  onDisconnect,
}: {
  state: DeliveryIntegrationListState
  integrations: DeliveryIntegrationView[]
  testingId?: string
  testPending: boolean
  togglingId?: string
  togglePending: boolean
  disconnectingId?: string
  disconnectPending: boolean
  onEdit: (integration: DeliveryIntegrationView) => void
  onTest: (integrationId: string) => void
  onToggle: (integrationId: string, active: boolean) => void
  onDisconnect: (integrationId: string) => void
}) {
  const tUi = useTranslations("Deliveries.DeliveriesDeliveryIntegrationCard")

  switch (state) {
    case "loading":
      return (
        <div className="animate-pulse space-y-2 motion-reduce:animate-none">
          <div className="h-20 rounded bg-muted" />
        </div>
      )
    case "empty":
      return (
        <p className="text-sm text-muted-foreground">
          <span key={tUi("noDeliveryIntegrationsConnectedYet")}>{tUi("noDeliveryIntegrationsConnectedYet")}</span></p>
      )
    case "ready":
      return (
        <div className="space-y-2">
          {integrations.map(integration => (
            <DeliveryIntegrationItem
              key={integration.id}
              integration={integration}
              pendingAction={getPendingAction({
                integrationId: integration.id,
                testingId,
                testPending,
                togglingId,
                togglePending,
                disconnectingId,
                disconnectPending,
              })}
              onEdit={onEdit}
              onTest={onTest}
              onToggle={onToggle}
              onDisconnect={onDisconnect}
            />
          ))}
        </div>
      )
    default: {
      const exhaustiveState: never = state
      return exhaustiveState
    }
  }
}

function getIntegrationListState(
  isLoading: boolean,
  integrationCount: number,
): DeliveryIntegrationListState {
  if (isLoading) return "loading"
  if (integrationCount === 0) return "empty"
  return "ready"
}

function getPendingAction(input: {
  integrationId: string
  testingId?: string
  testPending: boolean
  togglingId?: string
  togglePending: boolean
  disconnectingId?: string
  disconnectPending: boolean
}): "test" | "toggle" | "disconnect" | null {
  if (input.testPending && input.testingId === input.integrationId) return "test"
  if (input.togglePending && input.togglingId === input.integrationId) return "toggle"
  if (input.disconnectPending && input.disconnectingId === input.integrationId) {
    return "disconnect"
  }
  return null
}
